import { request } from './api.js';

/** @graph-connects none */
export interface QaResponsibilityListItem {
  issueKey: string;
  evaluatedAt: string;
  evaluator: string;
  bugType: string;
  qaResponsibility: string;
  isQaReviewed: boolean;
  qaReviewedBy: string | null;
  summary: string;
  responsibleTeam: string;
  causeProjectKey: string;
  causeProjectSummary: string;
  devLeaderGroup: string;
  devLeaderTeam: string;
}

/** @graph-connects none */
export interface QaResponsibilityDetail {
  issueKey: string;
  evaluatedAt: string;
  evaluator: string;
  bugType: string;
  qaResponsibility: string;
  qaResponsibilityReason: string;
  isQaReviewed: boolean;
  qaReviewedBy: string | null;
  qaReviewedAt: string | null;
  issueSummary: string;
  incidentLevel: string;
  service: string;
  causeProjectKey: string;
  bugSummary: string;
  expectedResult: string;
  actualResult: string;
  bugReason: string;
  stepsToReproduce: string;
  sourceLocale?: 'ja' | 'en';
  isTranslated?: boolean;
}

/** @graph-connects none */
export interface QaResponsibilityConfirmInput {
  qaResponsibility?: '有' | '無';
  qaResponsibilityReason?: string;
  locale?: 'ja' | 'en';
}

/**
 * QA責任評価APIクライアント。一覧取得・詳細取得・確定更新を提供
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA責任評価APIクライアント。一覧・詳細取得とQA責任確定（Backlog書き戻し）を提供
 * @graph-connects /qa-responsibilities [calls] QA責任評価一覧・詳細・確定APIエンドポイント
 */
export const qaResponsibilityApi = {
  getList: async (params?: {
    startDate?: string;
    endDate?: string;
    qaReviewStatus?: string;
    team?: string;
    locale?: string;
  }): Promise<{ data: QaResponsibilityListItem[]; total: number }> => {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.qaReviewStatus) query.set('qaReviewStatus', params.qaReviewStatus);
    if (params?.team) query.set('team', params.team);
    if (params?.locale) query.set('locale', params.locale);
    const qs = query.toString();
    return request<{ data: QaResponsibilityListItem[]; total: number }>(
      `/qa-responsibilities${qs ? `?${qs}` : ''}`,
    );
  },
  getDetail: async (
    issueKey: string,
    locale?: string,
  ): Promise<{ data: QaResponsibilityDetail }> => {
    const query = locale ? `?locale=${locale}` : '';
    return request<{ data: QaResponsibilityDetail }>(
      `/qa-responsibilities/${encodeURIComponent(issueKey)}${query}`,
    );
  },
  confirm: async (
    issueKey: string,
    data: QaResponsibilityConfirmInput,
  ): Promise<{ success: boolean; data: QaResponsibilityDetail; backlogWriteBack: boolean }> => {
    return request<{ success: boolean; data: QaResponsibilityDetail; backlogWriteBack: boolean }>(
      `/qa-responsibilities/${encodeURIComponent(issueKey)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  },
};
